import { CustomerCategoryEnum } from "../../SalesLead/Lead.model";
import { DayPatternEntry } from "./day-pattern";

export enum JobFrequencyEnum {
  WEEKLY = "WEEKLY",
  MONTHLY = "MONTHLY",
  YEARLY = "YEARLY",
}

export type TJobFormData = {
  leadId: string | null;
  name: string;
  zoneCode: number | null;
  employeeIds: string[];
  supervisorIds: string[];
  additionalNotes: string;
  invoiceGeneration: string;
  jobType: string;
  paymentsTerm: string;
  recurrence: boolean;
  frequency: JobFrequencyEnum;
  recurEvery: number | null;
  daysOfWeek: number[];
  dayPattern: DayPatternEntry[];
  dayOfMonth: number;
  monthOfYear: number;
  timeZone: string;
  attachments: File[];
  startTime: Date | null;
  lateInAlertTime: number | null;
  endTime: Date | null;
  lateOutAlertTime: number | null;
  recurrenceEndDate: Date | null;
  attachment2Path: File[];
  //view only
  customerCategory: CustomerCategoryEnum;
  clientId: number;
  clientFirstName: string;
  clientLastName: string;
  clientCompanyName: string;
  clientContactName: string;
  clientFax: string;
  clientPhoneNumber: string;
  cientContactEmail: string;
  leadAddress: string;
  leadlaborGoalHours: number | null;
  attachment1: string;
  leadAdditionalNotes: { category: string; names: string[] }[];
  sowFrequencies: { sowId: number, name: string, frequency: string }[];
};

/**
 * Whether the per-weekday pattern - not the job-level Employees field and the
 * job's own hours - is what the job should be built from.
 *
 * One predicate for all three places that must agree: the form (which field to
 * show), the schema (what to validate) and the request body (what to send). A
 * pattern only means anything on a weekly recurrence, and a `dayPattern` left
 * behind in form state after the user unchecks Recurrence or switches to
 * Monthly must not be validated or sent.
 *
 * It cannot see `isScheduleEdit` - the schema has no access to it - so callers
 * on the shared ScheduleForm gate on that themselves.
 */
export const isDayPatternInUse = ({
  recurrence,
  frequency,
  dayPattern,
}: {
  recurrence?: boolean;
  frequency?: JobFrequencyEnum;
  dayPattern?: DayPatternEntry[];
}): boolean =>
  Boolean(recurrence) &&
  frequency === JobFrequencyEnum.WEEKLY &&
  Array.isArray(dayPattern) &&
  dayPattern.length > 0;

/**
 * The IANA zone to send with a job. The backend checks the field with luxon's
 * IANAZone.isValidZone and 400s the whole create on anything else. Nothing in
 * the form sets the field any more, but a draft saved before the Job details
 * tab's Time Zone dropdown was removed still restores one of that control's
 * placeholder values ("randomValue1" ... "randomValue12"), and loadJobDraft
 * spreads a stored draft in wholesale - so a value held in the form cannot be
 * trusted. Anything the browser's own Intl cannot resolve is replaced with the
 * browser's zone rather than sent.
 */
export const resolveTimeZone = (value?: string): string => {
  try {
    // Throws RangeError on an unknown zone; the option is only read for that.
    new Intl.DateTimeFormat("en-US", { timeZone: value });

    if (value) {
      return value;
    }
  } catch {
    // Fall through to the browser's own zone.
  }

  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const frequencyLabelsByValue = {
  [JobFrequencyEnum.WEEKLY]: "week(s)",
  [JobFrequencyEnum.MONTHLY]: "month(s)",
  [JobFrequencyEnum.YEARLY]: "year(s)",
};